import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { Checkbox, Dropdown, Tooltip } from 'freemium-ui';
import { Box, Flex } from '@rebass/grid';
import { navigate } from '@reach/router';
import Icon from 'components/Icons';
import request from 'helpers/request';
import { i18nTimezones } from 'react-timezone-select';
import { PRIVACY_OPTIONS } from 'components/CreateIncident/constant';
import { useTranslation } from 'react-i18next';
import FormikDateTime from '../FormikDateTime';
import FormikAutomation from '../FormikAutomation';
import IncidentSource from './FormikIncidentSource';

export { FormikDateTime, IncidentSource, FormikAutomation };

const CustomSelect = styled.div`
  display: flex;
  align-items: center;
  span:first-child {
    display: grid;
    place-content: center;
    margin-left: 6px;
  }
`;

const customRender = item => (
  <div>
    <span>{item.label}</span>
    <br />
    <span style={{ color: '#596684' }}>{item.desc}</span>
  </div>
);
const itemToString = item => (
  <CustomSelect>
    {item.id === 'private' ? (
      <Icon glyph="private-incident" size={12} viewBox="0 0 16 16" />
    ) : (
      <Icon glyph="public-incident" size={14} viewBox="0 0 14 14" />
    )}
    <span style={{ marginLeft: '6px' }}>{item.label}</span>
  </CustomSelect>
);

export const TZInfo = ({ timezone, isPlanned, hideLabel, ...rest }) => {
  const { t } = useTranslation();
  const time = timezone ? i18nTimezones[timezone] : null;
  const changeTimezone = () => {
    window.open(`/admin/settings/accounts/`, '_blank');
  };
  return (
    <TimezoneContainer>
      <Flex width={1} justifyContent="space-between" className="content">
        {hideLabel ? (
          <span />
        ) : (
          `${
            isPlanned
              ? t('components.createIncident.scheduledFor')
              : t('components.createIncident.incidentTime')
          }`
        )}
        <Flex>
          <span>
            {t('components.createIncident.timezone')}{' '}
            <b>{time || timezone}&nbsp;</b>
          </span>

          <Tooltip
            position="top"
            label={t('components.createIncident.openAccountsToChange')}
          >
            <button type="button" onClick={changeTimezone}>
              {t('components.createIncident.change')}
            </button>
          </Tooltip>
        </Flex>
      </Flex>
    </TimezoneContainer>
  );
};

export const Duration = props => (
  <Field name={props.name}>
    {({
      form: {
        values: { start, end }
      }
    }) => {
      const startTime = moment(start, moment.ISO_8601).isValid()
        ? moment.tz(start, props.timezone).format('YYYY-MM-DDTHH:mm:ss')
        : start;
      const endTime = moment(end, moment.ISO_8601).isValid()
        ? moment.tz(end, props.timezone).format('YYYY-MM-DDTHH:mm:ss')
        : end;
      return (
        <StyledDuration>
          <div className="title">Duration: </div>
          <span>
            {moment(endTime).diff(moment(startTime), 'minutes')} minutes
          </span>
        </StyledDuration>
      );
    }}
  </Field>
);
export const FormikDropdown = ({ name, ...rest }) => (
  <Field name={name}>
    {({ form }) => (
      <>
        <Dropdown
          name={name}
          defaultSelectedItem={form.values[name]}
          onChange={item => form.setFieldValue(name, item)}
          {...rest}
        />
      </>
    )}
  </Field>
);
export const FormikCheckbox = ({
  name,
  label,
  disabled,
  tipTxt,
  tipPosition
}) => (
  <Field name={name}>
    {({ form }) => (
      <Box
        mx="1"
        my="1"
        style={name === 'send_email' ? { marginRight: '40px' } : {}}
      >
        <Checkbox
          name={name}
          disabled={disabled}
          value={form.values[name]}
          onChange={() => {
            form.setFieldValue(name, !form.values[name]);
          }}
          checked={form.values[name]}
        >
          {label}
          {tipTxt ? (
            <Tooltip position={tipPosition} label={tipTxt}>
              <span
                style={{
                  position: 'relative',
                  top: '3px',
                  paddingLeft: '5px'
                }}
              >
                <Icon glyph="info" size={14} viewBox="0 0 12 10" />
              </span>
            </Tooltip>
          ) : null}
        </Checkbox>
      </Box>
    )}
  </Field>
);

export const FormikTwitter = props => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const accounts = await request('/twitter-accounts/');
      setAccounts(accounts.results);
      setLoading(false);
    })();
  }, []);

  const navigateToTwitter = () => {
    if (props.close) {
      props.close();
    }
    navigate(`/admin/settings/integration/`);
  };
  return (
    <StyledTweetNotification>
      <FormikCheckbox
        {...props}
        loading={loading}
        disabled={loading || !accounts.length || props.disabled}
      />
      {loading
        ? null
        : (loading || !accounts.length || props.disabled) && (
            <div
              role="none"
              className="connect-link"
              onClick={navigateToTwitter}
            >
              {t('components.createIncident.connectToTwitter')}
            </div>
          )}
    </StyledTweetNotification>
  );
};

export const FormikPrivacy = props => (
  <FormikDropdown
    name="privacy"
    itemToString={itemToString}
    itemComponent={customRender}
    items={PRIVACY_OPTIONS}
    overrideButtonStyles={{
      background: '#F5F7F9',
      border: 'none',
      minHeight: '30px'
    }}
    overrideContainerStyles={{ minWidth: '238px' }}
    width="100px"
    disabled={props.disabled}
  />
);

const StyledTweetNotification = styled(Flex)`
  align-items: center;
  .connect-link {
    cursor: pointer;
    display: inline;
    line-height: 2;
    text-decoration: none;
    color: rgb(0, 122, 255);
    cursor: pointer;
    font-size: 12px;
    opacity: 1;
  }
`;

const StyledDuration = styled(Box)`
  display: flex;
  align-items: center;
  .title {
    font-size: 12px;
    padding-left: 4px;
    padding-right: 4px;
  }
  span {
    font-weight: 400;
    font-size: 13px;
  }
`;
const TimezoneContainer = styled.div`
  .content {
    padding-bottom: 8px;
    padding-left: 2px;
    font-size: 12px;
    font-weight: 500;
    button {
      cursor: pointer;
      background: #fff;
      border: 0;
      color: #5984ab;
    }
    span {
      font-weight: 400;
      padding-left: 5px;
      font-size: 12px;
      b {
        font-weight: 500;
      }
    }
  }
`;
